import { ICSVRow } from 'interfaces/ICSV';
import { IPlayWithBallFinal, IMergedPlayWithBallFinal } from 'interfaces/sessionVerification/plays/IPlayWithBallFinal';
import { ISession } from 'interfaces/session/ISession';
import { IBallFinal, baseballLogicService, IPlayer } from '@trackman/baseball-shared-components';
import { createNewPlayBasedOnRow } from './createPlayBasedOnRow';
import { mergeRowAndPlay } from './mergeRowAndPlay';
import { undefinedValue } from '../../constants/Constants';

export default (
  csvRows: Array<ICSVRow>,
  plays: Array<IPlayWithBallFinal>,
  session: ISession,
  ballFinals: IBallFinal[],
  homeTeamRoster: IPlayer[],
  awayTeamRoster: IPlayer[]
): Array<IMergedPlayWithBallFinal> => {
  const mergedPlays: Array<IMergedPlayWithBallFinal> = [];
  const usedPlayIds = [] as string[];
  csvRows.forEach((row) => {
    fixRow(row);
    if (row.gameuid !== session.id) {
      throw Error(`CSV file doesn't match Session: ${session.id}`);
    }
    if (!row.pitchuid) {
      mergedPlays.push(createNewPlayBasedOnRow(row, session, ballFinals, homeTeamRoster, awayTeamRoster));
    } else {
      const play = plays.find((play) => play.play && play.play.id === row.pitchuid);
      if (!play) {
        throw Error(`PitchUID: ${row.pitchuid} does not match any play Id`);
      }
      if (play) {
        mergedPlays.push(mergeRowAndPlay(row, play, session, homeTeamRoster, awayTeamRoster));
        usedPlayIds.push(play.play.id);
      }
    }
  });

  mergeDeletedAndWarmupPlays(plays, usedPlayIds, mergedPlays);
  updateSequenceNumbers(mergedPlays);
  calculateComputedProperties(mergedPlays);
  return mergedPlays;
};

const fixRow = (row: ICSVRow) => {
  row.batterid = row.batterid ? row.batterid.toString() : '';
  row.pitcherid = row.pitcherid ? row.pitcherid.toString() : '';
  row.catcherid = row.catcherid ? row.catcherid.toString() : '';
  row.batterside = row.batterside || undefinedValue;
  row.pitcherthrows = row.pitcherthrows || undefinedValue;
  row.batter = row.batter ? row.batter.toString() : '';
  row.pitcher = row.pitcher ? row.pitcher.toString() : '';
  row.catcher = row.catcher ? row.catcher.toString() : '';
  row.taggedhittype = row.taggedhittype || undefinedValue;
  row.korbb = row.korbb || undefinedValue;
  row.notes = row.notes || '';
  row.utcdatetime = row.utcdatetime || '';
  row.localdatetime = row.localdatetime || '';
  row.pitcherteam = row.pitcherteam ? row.pitcherteam.toString() : '';
  row.batterteam = row.batterteam ? row.batterteam.toString() : '';
  row.catcherteam = row.catcherteam ? row.catcherteam.toString() : '';
  row.taggedpitchtype = row.taggedpitchtype || undefinedValue;
  row.playresult = row.playresult || undefinedValue;
  row.balls = row.balls || 0;
  row.strikes = row.strikes || 0;
  row.outs = row.outs || 0;
  row.outsonplay = row.outsonplay || 0;
};

const mergeDeletedAndWarmupPlays = (plays: IPlayWithBallFinal[], usedPlayIds: string[], mergedPlays: IMergedPlayWithBallFinal[]) => {
  plays.forEach((play) => {
    if (!usedPlayIds.some((p) => p === play.play.id)) {
      const newPlay = {
        ballFinals: [],
        isChanged: false,
        isBallTrackingChanged: false,
        isPlayerTrackingChanged: false,
        play: { ...play.play },
        isMarkedForDeletion: false,
        isCreated: false,
        changedValues: [],
      };
      if (!newPlay.play.taggerBehavior.isDeleted && !newPlay.play.taggerBehavior.isWarmup) {
        newPlay.play.taggerBehavior.isDeleted = true;
        newPlay.play.taggerBehavior.playSequenceNumber = undefined;
        newPlay.isChanged = true;
        newPlay.isBallTrackingChanged = true;
        newPlay.isPlayerTrackingChanged = true;
        newPlay.isMarkedForDeletion = true;
      }
      mergedPlays.splice(newPlay.play.taggerBehavior.sequenceNumber - 1, 0, newPlay);
    }
  });
};

export const updateSequenceNumbers = (mergedPlays: IMergedPlayWithBallFinal[]) => {
  mergedPlays.forEach((p, i) => {
    if (p.play.taggerBehavior.sequenceNumber !== i + 1) {
      p.changedValues.push({
        propName: 'sequenceNumber',
        csvValue: (i + 1).toString(),
        playValue: p.play.taggerBehavior.sequenceNumber.toString(),
      });
      p.play.taggerBehavior.sequenceNumber = i + 1;
      p.isChanged = true;
      p.isBallTrackingChanged = true;
      p.isPlayerTrackingChanged = true;
    }
  });
};

export const calculateComputedProperties = (plays: IMergedPlayWithBallFinal[]) => {
  let previousPlayIndex = -1;
  let expectedSequenceNumber = 1;

  plays.forEach((play, index) => {
    if (play.play.taggerBehavior.isDeleted || play.play.taggerBehavior.isWarmup) {
      return;
    }
    if (play.play.taggerBehavior.playSequenceNumber !== expectedSequenceNumber) {
      play.changedValues.push({
        propName: 'playSequenceNumber',
        csvValue: expectedSequenceNumber.toString(),
        playValue: JSON.stringify(play.play.taggerBehavior.playSequenceNumber),
      });
      play.play.taggerBehavior.playSequenceNumber = expectedSequenceNumber;
      play.isChanged = true;
      play.isBallTrackingChanged = true;
      play.isPlayerTrackingChanged = true;
    }
    const { pAofInning, pitchOfPA } = baseballLogicService.calculatePlateAppearance(
      play.play,
      previousPlayIndex !== -1 ? plays[previousPlayIndex].play : undefined
    );
    if (play.play.taggerBehavior.pAofInning !== pAofInning) {
      play.changedValues.push({
        propName: 'pAofInning',
        csvValue: pAofInning.toString(),
        playValue: JSON.stringify(play.play.taggerBehavior.pAofInning),
      });
      play.play.taggerBehavior.pAofInning = pAofInning;
      play.isChanged = true;
      play.isBallTrackingChanged = true;
    }
    if (play.play.taggerBehavior.pitchOfPA !== pitchOfPA) {
      play.changedValues.push({
        propName: 'pitchOfPA',
        csvValue: pitchOfPA.toString(),
        playValue: JSON.stringify(play.play.taggerBehavior.pitchOfPA),
      });
      play.play.taggerBehavior.pitchOfPA = pitchOfPA;
      play.isChanged = true;
      play.isBallTrackingChanged = true;
    }

    calculateScore(play, previousPlayIndex !== -1 ? plays[previousPlayIndex] : undefined);

    previousPlayIndex = index;
    expectedSequenceNumber++;
  });
};

const calculateScore = (play: IMergedPlayWithBallFinal, prevPlay?: IPlayWithBallFinal) => {
  let homeTeamScore;
  let awayTeamScore;
  if (!prevPlay) {
    if (play.play.gameState.isHomeTeamBatting) {
      homeTeamScore = play.play.playResult.runs;
      awayTeamScore = 0;
    } else {
      awayTeamScore = play.play.playResult.runs;
      homeTeamScore = 0;
    }
  } else if (play.play.gameState.isHomeTeamBatting) {
    homeTeamScore = prevPlay.play.gameState.homeTeamScore + play.play.playResult.runs;
    awayTeamScore = prevPlay.play.gameState.awayTeamScore;
  } else {
    awayTeamScore = prevPlay.play.gameState.awayTeamScore + play.play.playResult.runs;
    homeTeamScore = prevPlay.play.gameState.homeTeamScore;
  }
  if (homeTeamScore !== play.play.gameState.homeTeamScore) {
    play.changedValues.push({
      propName: 'homeTeamScore',
      csvValue: homeTeamScore.toString(),
      playValue: play.play.gameState.homeTeamScore.toString(),
    });
    play.play.gameState.homeTeamScore = homeTeamScore;
    play.isChanged = true;
    play.isBallTrackingChanged = true;
  }
  if (awayTeamScore !== play.play.gameState.awayTeamScore) {
    play.changedValues.push({
      propName: 'awayTeamScore',
      csvValue: awayTeamScore.toString(),
      playValue: play.play.gameState.awayTeamScore.toString(),
    });
    play.play.gameState.awayTeamScore = awayTeamScore;
    play.isChanged = true;
    play.isBallTrackingChanged = true;
  }
};
